import { BUSY_RETRY_HINT, BUSY_TITLE, CHECK_LIST_HINT } from './create-busy';

/** What the button of a failure does; the screen that shows it supplies the handler. `none`: the copy has no button. */
export type FailureAction = 'none' | 'retry' | 'sign-in' | 'back' | 'check-list' | 'focus-name' | 'check-now' | 'go-service';

export interface FailureCopy {
  title: string;
  body?: string;
  /** The button's label; absent when the copy has none. */
  action?: { id: Exclude<FailureAction, 'none'>; label: string };
  /** `danger`: something failed; `warning`: the service is busy; `info`: nothing is wrong yet. */
  tone: 'danger' | 'warning' | 'info';
}

/** What the failure is about. Every field is optional: a missing one reads as a neutral word, never as `undefined`. */
export interface FailureSubject {
  /** One thing, lower case: `machine`. */
  thing?: string;
  /** Several, lower case: `machines`. */
  things?: string;
  /** Whose: `Acme`, or `your own organization`. */
  org?: string;
  /** What the person tried, lower case: `delete`. */
  verb?: string;
  /** The name they typed (a create 409). */
  name?: string;
  /** `thing` is a plural (`instances`): `aren't`, not `isn't`. */
  plural?: boolean;
}

/** `read`: a page or list; `action`: a button on something that exists; `create`: a form that makes a new thing. */
export type FailureContext = 'read' | 'action' | 'create';

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const administrator = (org?: string) => (org ? `an administrator of ${org}` : 'an administrator');

/** The row of the copy table for a status. `undefined` is no answer at all (network). Other statuses read as the generic rows. */
export function failureCopy(context: FailureContext, status: number | undefined, subject: FailureSubject = {}): FailureCopy {
  const { thing = 'item', things = 'items', org, verb, name, plural = false } = subject;
  const be = plural ? 'are' : 'is';
  const reading = context === 'read';
  if (status === 401) return sessionEnded();
  if (status === 429 || status === 503) {
    if (reading) return { tone: 'warning', title: BUSY_TITLE, body: 'Wait a few seconds, then try again.', action: { id: 'retry', label: 'Try again' } };
    // 429 refused the request (safe to repeat); a 503 may have been answered after it went through: create-busy's words.
    if (status === 429) return { tone: 'warning', title: BUSY_TITLE, body: BUSY_RETRY_HINT, action: { id: 'retry', label: 'Try again' } };
    return { tone: 'warning', title: BUSY_TITLE, body: CHECK_LIST_HINT, action: { id: 'check-list', label: 'Check the list' } };
  }
  if (status === 403) {
    if (reading) {
      return {
        tone: 'danger',
        title: `You don't have access to ${thing}`,
        body: `Ask ${administrator(org)} to give you access.`,
        action: { id: 'back', label: `Back to ${things}` },
      };
    }
    return { tone: 'danger', title: `You don't have permission to ${verb ? `${verb} this ${thing}` : 'do this'}`, body: `Ask ${administrator(org)}.` };
  }
  if (status === 404) {
    if (reading) {
      return { tone: 'danger', title: `${capitalize(thing)} ${be}n't available`, body: 'It was removed, or your access to it ended.', action: { id: 'back', label: `Back to ${things}` } };
    }
    return { tone: 'danger', title: `${capitalize(thing)} no longer ${plural ? 'exist' : 'exists'}`, body: 'It may have been deleted by someone else.', action: { id: 'back', label: `Back to ${things}` } };
  }
  if (status === 409) {
    if (context === 'create') {
      return { tone: 'danger', title: `A ${thing} named "${name ?? ''}" already exists`, body: 'Choose another name.', action: { id: 'focus-name', label: 'Choose another name' } };
    }
    if (context === 'action') {
      return { tone: 'warning', title: `${capitalize(thing)} ${be} busy`, body: "It's changing state. Wait for it to finish, then try again.", action: { id: 'retry', label: 'Try again' } };
    }
  }
  if (status === undefined) {
    return { tone: 'danger', title: "Can't reach the service", body: 'Check your connection, then try again.', action: { id: 'retry', label: 'Try again' } };
  }
  // The request itself was not accepted (400, 405, 410, 413, 422...): trying the same thing again cannot help, so no Try again on a write.
  if (status >= 400 && status < 500 && !reading) {
    return { tone: 'danger', title: "That didn't go through", body: 'Check what you entered and try again.' };
  }
  if (reading) {
    return { tone: 'danger', title: `Couldn't load ${things}`, body: "This is usually temporary. Your data isn't affected.", action: { id: 'retry', label: 'Try again' } };
  }
  return { tone: 'danger', title: 'Something went wrong on our side', body: 'Try again. If it keeps happening, contact support.', action: { id: 'retry', label: 'Try again' } };
}

/** 401: the sign-in ended. */
export function sessionEnded(): FailureCopy {
  return { tone: 'info', title: 'Your session expired', body: 'Sign in again to continue.', action: { id: 'sign-in', label: 'Sign in' } };
}

/** The two lists of what other organizations shared (workspaces, projects) could not be read. */
export function sharedWorkspacesFailed(): FailureCopy {
  return {
    tone: 'danger',
    title: "Couldn't load your shared workspaces",
    body: 'Try again. If this keeps happening, you can still use your own organization.',
    action: { id: 'retry', label: 'Try again' },
  };
}

/** The row of a switcher group whose projects could not be read; `org` is unknown when the list that names it failed. */
export function foreignProjectsRow(org?: string): FailureCopy {
  return { tone: 'danger', title: org ? `Couldn't load projects of ${org}.` : "Couldn't load shared projects.", action: { id: 'retry', label: 'Try again' } };
}

/** A grant that foundation lists and the service does not yet. */
export function grantLag(): FailureCopy {
  return {
    tone: 'info',
    title: 'Your access was just granted',
    body: "It's still being applied. This can take up to a minute. We'll keep checking.",
    action: { id: 'check-now', label: 'Check now' },
  };
}

/** E5: the workspace in the URL is not one the person has. */
export function workspaceUnavailable(service: string): FailureCopy {
  return {
    tone: 'danger',
    title: "This workspace isn't available",
    body: `You don't have access to it, or it was removed. Go to ${service} to choose another.`,
    action: { id: 'go-service', label: `Go to ${service}` },
  };
}

/** The toast when the person's access to a project ended while they were working in it. */
export function revokedToastText(project: string, org: string): string {
  return `You no longer have access to ${project} in ${org}`;
}

/** The permission check itself failed (not a refusal). */
export function permissionsCheckFailed(): FailureCopy {
  return { tone: 'warning', title: "Couldn't check your permissions", body: 'Actions are unavailable until we can.', action: { id: 'retry', label: 'Try again' } };
}

/** A 403 on a page: what the person may not do, and who can change it. */
export function permissionDenied(verb: string, org?: string): FailureCopy {
  return { tone: 'danger', title: `You don't have permission to ${verb}`, body: `Ask ${administrator(org)}.` };
}

/** The org name as the sentence wants it: `your own organization` when it is the person's own name (a personal organization). */
export function organizationPhrase(orgName: string | undefined, personName: string | undefined): string | undefined {
  const org = orgName?.trim();
  if (!org) return undefined;
  const person = personName?.trim();
  return person && org.toLowerCase() === person.toLowerCase() ? 'your own organization' : org;
}

/** One line for a toast or a message that has no room for a body: `Title. Body`. */
export function failureLine(copy: FailureCopy): string {
  const title = /[.!?]$/.test(copy.title) ? copy.title : `${copy.title}.`;
  return copy.body ? `${title} ${copy.body}` : title;
}
